import React, { useMemo, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';
import { Plus, Search } from 'lucide-react';
import type { BuildingCheckTemplate } from '../../../../BuildingChecks/types';
import { INTERVAL_UNITS, TEMPLATE_CATEGORIES } from '../../../../BuildingChecks/types';
import type { BuildingCheck, IntervalUnit } from '../../../../BuildingChecks/buildingChecksUtils';
import { formatInterval } from '../../../../BuildingChecks/buildingChecksUtils';
import { addInterval, DateInterval, formatDateOnlyForStorage } from '../../../../../Helpers/DateUtils';
import type { NewComponentCheck } from '../../../../../hooks/inspections/useComponentChecks';
import NumberInput from '../../../../../Components/NumberInput';

interface AddCheckModalProps {
    componentName: string;
    /**
     * Massredigering: how many of the selected components a template already
     * covers (they are skipped) out of how many are selected
     */
    alreadyLinked?: (templateId: string) => { linked: number; total: number };
    /** Templates the component can be linked to */
    templates: BuildingCheckTemplate[];
    /** The building's checks, keyed by check key */
    instances: Record<string, BuildingCheck>;
    /** How many components get a check; a label is only offered for one */
    componentCount: number;
    /** Admins may create a new template from here */
    canCreateTemplate: boolean;
    /** Opens straight on the new-check step for this template (after "Skapa ny mall") */
    initialTemplate?: BuildingCheckTemplate | null;
    /** Create a check of the template for each component */
    onCreate: (template: BuildingCheckTemplate, settings: NewComponentCheck) => Promise<boolean>;
    onCreateTemplate: () => void;
    onClose: () => void;
}

type Step = 'pick' | 'new';

const defaultFirstDueDate = (interval: DateInterval): string =>
    addInterval(formatDateOnlyForStorage(new Date()), interval);

/**
 * Give the component(s) a check. After picking a template the user sets the
 * interval and first due date; one check is created per component (Massredigering:
 * per selected component the template does not cover yet).
 */
export const AddCheckModal: React.FC<AddCheckModalProps> = ({
    componentName,
    alreadyLinked,
    templates,
    instances,
    componentCount,
    canCreateTemplate,
    initialTemplate = null,
    onCreate,
    onCreateTemplate,
    onClose
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [template, setTemplate] = useState<BuildingCheckTemplate | null>(initialTemplate);
    const [step, setStep] = useState<Step>(initialTemplate ? 'new' : 'pick');
    const [intervalValue, setIntervalValue] = useState(initialTemplate?.interval.value ?? 1);
    const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>(initialTemplate?.interval.unit ?? 'years');
    const [firstDueDate, setFirstDueDate] = useState(initialTemplate ? defaultFirstDueDate(initialTemplate.interval) : '');
    const [label, setLabel] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const groups = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const matching = term ? templates.filter(t => t.name.toLowerCase().includes(term)) : templates;
        return TEMPLATE_CATEGORIES
            .map(category => ({ ...category, templates: matching.filter(t => t.category === category.value) }))
            .filter(group => group.templates.length > 0);
    }, [templates, searchTerm]);

    const countOf = (templateId: string) =>
        Object.values(instances).filter(check => check.templateId === templateId).length;

    const startNew = (picked: BuildingCheckTemplate) => {
        setIntervalValue(picked.interval.value);
        setIntervalUnit(picked.interval.unit);
        setFirstDueDate(defaultFirstDueDate(picked.interval));
        setLabel('');
        setStep('new');
    };

    const pick = (picked: BuildingCheckTemplate) => {
        setTemplate(picked);
        setError(null);
        startNew(picked);
    };

    const run = async (action: () => Promise<boolean>, failure: string) => {
        setSaving(true);
        setError(null);
        const ok = await action();
        setSaving(false);
        if (ok) {
            onClose();
        } else {
            setError(failure);
        }
    };

    const back = () => {
        setError(null);
        setTemplate(null);
        setStep('pick');
    };

    const dateIsValid = /^\d{4}-\d{2}-\d{2}$/.test(firstDueDate);

    // Massredigering: the selected components the picked template already covers are left as they are
    const skipped = template && alreadyLinked ? alreadyLinked(template.id) : null;
    const skippedNote = skipped && skipped.linked > 0 && (
        <Alert variant="info" className="mb-3">
            {skipped.linked} av {skipped.total} valda komponenter har redan en kontroll av mallen och hoppas över.
        </Alert>
    );

    return (
        <Modal show onHide={onClose} centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title as="h5">Lägg till kontroll</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

                {step === 'pick' && (
                    <>
                        <p className="mb-2">
                            Ge <strong>{componentName}</strong> en kontroll som sköter arbetet.
                            Kontrollen gäller direkt och ingår inte i underhållsplanens kostnad.
                        </p>
                        <div className="input-group mb-3">
                            <span className="input-group-text"><Search size={16} aria-hidden="true" /></span>
                            <Form.Control
                                type="text"
                                placeholder="Sök mall..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="Sök mall"
                            />
                        </div>
                        {groups.length === 0 && (
                            <p className="text-muted">
                                {templates.length === 0 ? 'Det finns inga mallar att välja.' : 'Inga mallar matchar sökningen.'}
                            </p>
                        )}
                        {groups.map(group => (
                            <div key={group.value} className="mb-3">
                                <div className="fw-semibold small text-muted mb-1">{group.label}</div>
                                <div className="list-group">
                                    {group.templates.map(option => {
                                        const count = countOf(option.id);
                                        return (
                                            <button
                                                key={option.id}
                                                type="button"
                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2"
                                                onClick={() => pick(option)}
                                                disabled={saving}
                                            >
                                                <span>{option.name}</span>
                                                <span className="small text-muted text-nowrap">
                                                    {count > 0
                                                        ? `${count} ${count === 1 ? 'kontroll' : 'kontroller'} på byggnaden`
                                                        : `Var ${formatInterval(option.interval)}`}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </>
                )}

                {step === 'new' && template && (
                    <>
                        {skippedNote}
                        <p>
                            {componentCount === 1
                                ? <>En ny kontroll <strong>{template.name}</strong> skapas för <strong>{componentName}</strong>.</>
                                : <>En kontroll <strong>{template.name}</strong> skapas för var och en av komponenterna, med samma intervall och datum.</>}
                        </p>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="add-check-interval">Intervall</Form.Label>
                            <div className="d-flex gap-2">
                                <NumberInput
                                    id="add-check-interval"
                                    className="form-control"
                                    min="1"
                                    integer
                                    emptyValue={1}
                                    value={intervalValue}
                                    onChange={(value) => {
                                        const next = Math.max(1, value);
                                        setIntervalValue(next);
                                        setFirstDueDate(defaultFirstDueDate({ value: next, unit: intervalUnit }));
                                    }}
                                    style={{ maxWidth: '100px' }}
                                />
                                <Form.Select
                                    value={intervalUnit}
                                    onChange={(e) => {
                                        const unit = e.target.value as IntervalUnit;
                                        setIntervalUnit(unit);
                                        setFirstDueDate(defaultFirstDueDate({ value: intervalValue, unit }));
                                    }}
                                    aria-label="Intervallets enhet"
                                    style={{ maxWidth: '160px' }}
                                >
                                    {INTERVAL_UNITS.map(unit => (
                                        <option key={unit.value} value={unit.value}>{unit.label}</option>
                                    ))}
                                </Form.Select>
                            </div>
                            <Form.Text className="text-muted">Mallens intervall: {formatInterval(template.interval)}</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="add-check-first-due">Första förfallodatum</Form.Label>
                            <Form.Control
                                id="add-check-first-due"
                                type="date"
                                value={firstDueDate}
                                onChange={(e) => setFirstDueDate(e.target.value)}
                                isInvalid={!dateIsValid}
                            />
                            <Form.Control.Feedback type="invalid">Välj ett datum</Form.Control.Feedback>
                        </Form.Group>
                        {componentCount === 1 && (
                        <Form.Group>
                            <Form.Label htmlFor="add-check-label">Benämning (valfritt)</Form.Label>
                            <Form.Control
                                id="add-check-label"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                maxLength={100}
                                placeholder={componentName}
                            />
                            <Form.Text className="text-muted">Utan benämning visas komponentens namn.</Form.Text>
                        </Form.Group>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {step === 'pick' ? (
                    <>
                        {canCreateTemplate && (
                            <button
                                type="button"
                                className="btn btn-outline-primary me-auto d-flex align-items-center"
                                onClick={onCreateTemplate}
                                disabled={saving}
                            >
                                <Plus size={16} className="me-1" />
                                Skapa ny mall
                            </button>
                        )}
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                            Avbryt
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => (initialTemplate && step === 'new' ? onClose() : back())}
                            disabled={saving}
                        >
                            {initialTemplate && step === 'new' ? 'Avbryt' : 'Tillbaka'}
                        </button>
                        {step === 'new' && template && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => run(
                                    () => onCreate(template, {
                                        firstDueDate,
                                        interval: { value: intervalValue, unit: intervalUnit },
                                        label
                                    }),
                                    'Kunde inte skapa kontrollen. Försök igen.'
                                )}
                                disabled={saving || !dateIsValid}
                            >
                                {saving ? 'Sparar...' : 'Skapa kontroll'}
                            </button>
                        )}
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};
